import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  MenuItem,
  Grid
} from '@mui/material';
import { format } from 'date-fns';
import instance from '../utils/axiosConfig';
import { useSnackbar } from '../contexts/SnackbarContext';

const SubUserLogs = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    subUserId: '',
    startDate: '',
    endDate: ''
  });
  const { showSnackbar } = useSnackbar();

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });

      const response = await instance.get(`/api/subuser-logs?${params}`);
      setLogs(response.data.logs);
      setTotalLogs(response.data.pagination.totalLogs);
    } catch (error) {
      showSnackbar('Error al cargar los logs', 'error');
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setPage(0);
  };

  const formatActionType = (action) => {
    const actionMap = {
      'CREATE_PRODUCT': 'Crear Producto',
      'EDIT_PRODUCT': 'Editar Producto',
      'DELETE_PRODUCT': 'Eliminar Producto',
      'PAUSE_PRODUCT': 'Pausar Producto',
      'EDIT_DISTRIBUTOR_INFO': 'Editar Info Distribuidor',
      'CREATE_SUBUSER': 'Crear Sub-usuario',
      'EDIT_SUBUSER': 'Editar Sub-usuario',
      'DELETE_SUBUSER': 'Eliminar Sub-usuario'
    };
    return actionMap[action] || action;
  };

  const fieldTranslations = {
    // Campos generales
    name: 'Nombre',
    description: 'Descripción',
    status: 'Estado',
    phone: 'Teléfono',
    email: 'Correo electrónico',
    address: 'Dirección',
    
    // Campos de producto
    productName: 'Nombre del producto',
    productId: 'ID del producto',
    basePrice: 'Precio base',
    availableAmount: 'Cantidad disponible',
    brand: 'Marca',
    code: 'Código',
    category: 'Categoría',
    unit: 'Unidad',
    newStatus: 'Nuevo estado',
    oldStatus: 'Estado anterior',
    imageUrls: 'Imágenes',
    hidePrice: 'Ocultar precio',
    hideShippingFees: 'Ocultar costos de envío',
    
    // Campos de usuario
    subUserId: 'ID del sub-usuario',
    subUserName: 'Nombre del sub-usuario',
    permissions: 'Permisos',
    
    // Campos de distribuidor
    governmentName: 'Razón social',
    CUIT: 'CUIT',
    country: 'País',
    province: 'Provincia',
    city: 'Ciudad',
    zones: 'Zonas',
  };

  const valueTranslations = {
    // Estados
    active: 'Activo',
    inactive: 'Inactivo',
    pending: 'Pendiente',
    verified: 'Verificado',
    unverified: 'No verificado',
    
    // Booleanos
    true: 'Sí',
    false: 'No',
    
    // Permisos
    canCreateProducts: 'Crear productos',
    canEditProducts: 'Editar productos',
    canDeleteProducts: 'Eliminar productos',
    canPauseProducts: 'Pausar productos',
    canEditDistributorInfo: 'Editar información del distribuidor',
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return valueTranslations[value.toString()] || value.toString();
    if (Array.isArray(value)) {
      return value.map(v => valueTranslations[v] || v).join(', ');
    }
    return valueTranslations[value] || value.toString();
  };

  const formatDetails = (log) => {
    if (!log.details) return 'N/A';

    if (log.action === 'EDIT_DISTRIBUTOR_INFO' && log.details.changes) {
      return (
        <>
          <div><strong>Campos modificados:</strong> {
            log.details.fieldsModified
              .map(field => fieldTranslations[field] || field)
              .join(', ')
          }</div>
          {Object.entries(log.details.changes).map(([field, value]) => (
            <div key={field}>
              <strong>{fieldTranslations[field] || field}:</strong> {formatValue(value)}
            </div>
          ))}
        </>
      );
    }

    // Handle other types of logs
    return Object.entries(log.details)
      .filter(([key]) => key !== 'changes' && key !== 'fieldsModified')
      .map(([key, value]) => (
        <div key={key}>
          <strong>{fieldTranslations[key] || key}:</strong> {
            typeof value === 'object' 
              ? JSON.stringify(value, null, 2)
              : formatValue(value)
          }
        </div>
      ));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Logs de Actividad de Sub-usuarios
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            label="Tipo de Acción"
            value={filters.action}
            onChange={handleFilterChange('action')}
          >
            <MenuItem value="">Todas las acciones</MenuItem>
            <MenuItem value="CREATE_PRODUCT">Crear Producto</MenuItem>
            <MenuItem value="EDIT_PRODUCT">Editar Producto</MenuItem>
            <MenuItem value="DELETE_PRODUCT">Eliminar Producto</MenuItem>
            <MenuItem value="PAUSE_PRODUCT">Pausar Producto</MenuItem>
            <MenuItem value="EDIT_DISTRIBUTOR_INFO">Editar Info Distribuidor</MenuItem>
            <MenuItem value="CREATE_SUBUSER">Crear Sub-usuario</MenuItem>
            <MenuItem value="EDIT_SUBUSER">Editar Sub-usuario</MenuItem>
            <MenuItem value="DELETE_SUBUSER">Eliminar Sub-usuario</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            type="date"
            fullWidth
            label="Fecha Inicio"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate}
            onChange={handleFilterChange('startDate')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            type="date"
            fullWidth
            label="Fecha Fin"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate}
            onChange={handleFilterChange('endDate')}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sub-usuario</TableCell>
              <TableCell>Acción</TableCell>
              <TableCell>Detalles</TableCell>
              <TableCell>Fecha</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log._id}>
                <TableCell>{log.subUserId?.name || 'N/A'}</TableCell>
                <TableCell>{formatActionType(log.action)}</TableCell>
                <TableCell>{formatDetails(log)}</TableCell>
                <TableCell>
                  {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalLogs}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Box>
  );
};

export default SubUserLogs; 